import {
  DOMAIN_ZELFHULP_ALCOHOL,
  DOMAIN_ZELFHULP_GOKKEN,
  ENABLE_AIDA_SCREENING,
  ENABLE_BUDDY_CO_SCREENING,
  FEATURE_FLAGS,
  ROUTE_LOGIN_SEARCH_PARAM,
} from '../../../config/constants';
import getCurrentDomain from '../../../helpers/getCurrentDomain';
import type { RouterConfig, SelfHelpRouteName } from '../types';

// Pages.
import PageAccount from '../../../pages/Account';
import PageBadges from '../../../pages/Badges';
import PageBuddyInviteAccept from '../../../pages/BuddyInviteAccept';
import PageBuddyInviteRefuse from '../../../pages/BuddyInviteRefuse';
import PageBuddyUnsubscribe from '../../../pages/BuddyUnsubscribe';
import PageDailyRegistration from '../../../pages/DailyRegistration';
import PageDailyRegistrationUpdate from '../../../pages/DailyRegistrationUpdate';
import PageDiaryAdd from '../../../pages/Diary/Add';
import PageDiaryOverview from '../../../pages/Diary/Overview';
import PageForum from '../../../pages/Forum';
import PageForumCreatePost from '../../../pages/Forum/CreatePost';
import PageLogin from '../../../pages/Login';
import PageLogout from '../../../pages/Logout';
import PageOnboarding from '../../../pages/Onboarding';
import PageOnboardingAida from '../../../pages/OnboardingAida';
import PageOnboardingAidaConsent from '../../../pages/OnboardingAidaConsent';
import PageOnboardingBuddyCo from '../../../pages/OnboardingBuddyCo';
import PageBuddyCoChecklistRedirect from '../../../pages/OnboardingBuddyCo/ChecklistRedirect';
import PageOnboardingBuddyCoConsent from '../../../pages/OnboardingBuddyCoConsent';
import PageOnboardingHti from '../../../pages/OnboardingHti';
import PageRecoveryPlan from '../../../pages/RecoveryPlan';
import PageChangeRecoveryPlan from '../../../pages/ChangeRecoveryPlan';
import PageResetPassword from '../../../pages/ResetPassword';
import PageValidateEmail from '../../../pages/ValidateEmail';
import PageCalendar from '../../../pages/Calendar';

const routesConfigSelfHelp: RouterConfig<SelfHelpRouteName> = {
  onboardingBuddyCoChecklist: {
    basePath: '/registreren',
    nl: '/nl/registreren',
    type: 'default',
    component: PageBuddyCoChecklistRedirect,
    condition: () => ENABLE_BUDDY_CO_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_GOKKEN,
  },
  onboardingAida: {
    basePath: '/registreren/screening',
    nl: '/nl/registreren/screening',
    type: 'private',
    component: PageOnboardingAida,
    condition: () => ENABLE_AIDA_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_ALCOHOL,
  },
  onboardingAidaConsent: {
    basePath: '/registreren/toestemming',
    nl: '/nl/registreren/toestemming',
    type: 'private',
    component: PageOnboardingAidaConsent,
    condition: () => ENABLE_AIDA_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_ALCOHOL,
  },
  onboardingBuddyCo: {
    basePath: '/registreren/screening',
    nl: '/nl/registreren/screening',
    type: 'private',
    component: PageOnboardingBuddyCo,
    condition: () => ENABLE_BUDDY_CO_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_GOKKEN,
  },
  onboardingBuddyCoConsent: {
    basePath: '/registreren/toestemming',
    nl: '/nl/registreren/toestemming',
    type: 'private',
    component: PageOnboardingBuddyCoConsent,
    condition: () => ENABLE_BUDDY_CO_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_GOKKEN,
  },
  accountBase: {
    basePath: '/account',
    nl: '/nl/account',
    en: '/en/account',
    type: 'private',
    exact: false,
    component: PageAccount,
  },
  badges: {
    basePath: '/badges',
    nl: '/nl/badges',
    en: '/en/badges',
    type: 'private',
    component: PageBadges,
  },
  buddyInviteAccept: {
    basePath: '/buddy/accept/:pairingCode',
    nl: '/nl/buddy/accept/:pairingCode',
    en: '/en/buddy/accept/:pairingCode',
    type: 'public',
    component: PageBuddyInviteAccept,
  },
  buddyInviteRefuse: {
    basePath: '/buddy/refuse/:pairingCode',
    nl: '/nl/buddy/refuse/:pairingCode',
    en: '/en/buddy/refuse/:pairingCode',
    type: 'public',
    component: PageBuddyInviteRefuse,
  },
  buddyUnsubscribe: {
    basePath: '/buddy/unsubscribe/:pairingCode',
    nl: '/nl/buddy/unsubscribe/:pairingCode',
    en: '/en/buddy/unsubscribe/:pairingCode',
    type: 'public',
    component: PageBuddyUnsubscribe,
  },
  dailyRegistration: {
    basePath: '/dagelijkse-registratie',
    nl: '/nl/dagelijkse-registratie',
    en: '/en/daily-registration',
    type: 'private',
    component: PageDailyRegistration,
  },
  dailyRegistrationUpdate: {
    basePath: '/dagelijkse-registratie/:date',
    nl: '/nl/dagelijkse-registratie/:date',
    en: '/en/daily-registration/:date',
    type: 'private',
    component: PageDailyRegistrationUpdate,
  },
  diaryAdd: {
    basePath: '/dagboek/nieuwe-ervaring',
    nl: '/nl/dagboek/nieuwe-ervaring',
    en: '/en/diary/new-entry',
    type: 'private',
    component: PageDiaryAdd,
  },
  diaryOverview: {
    basePath: '/dagboek',
    nl: '/nl/dagboek',
    en: '/en/diary',
    type: 'private',
    component: PageDiaryOverview,
  },
  forumCreatePost: {
    basePath: '/forum/create/:category',
    nl: '/nl/forum/create/:category',
    en: '/en/forum/create/:category',
    type: 'private',
    component: PageForumCreatePost,
  },
  forumOverview: {
    basePath: '/forum',
    nl: '/nl/forum',
    en: '/en/forum',
    type: 'private',
    component: PageForum,
  },
  login: {
    basePath: '/inloggen',
    nl: '/nl/inloggen',
    en: '/en/login',
    type: 'public',
    component: PageLogin,
    search: `?${ROUTE_LOGIN_SEARCH_PARAM}`,
  },
  logout: {
    basePath: '/user/logout',
    nl: '/nl/user/logout',
    en: '/en/user/logout',
    type: 'private',
    component: PageLogout,
  },
  onboardingCredentials: {
    basePath: '/registreren/gegevens',
    nl: '/nl/registreren/gegevens',
    en: '/en/register/credentials',
    type: 'default',
    component: PageOnboarding,
  },
  onboardingCredentialsHti: {
    basePath: '/registreren-hti/gegevens',
    nl: '/nl/registreren-hti/gegevens',
    en: '/en/register-hti/credentials',
    type: 'private',
    component: PageOnboardingHti,
    condition: () => FEATURE_FLAGS.zelfhulp_hti,
  },
  recoveryPlan: {
    basePath: '/mijn-plan',
    nl: '/nl/mijn-plan',
    en: '/en/my-recovery-plan',
    type: 'private',
    component: PageRecoveryPlan,
  },
  changeRecoveryPlan: {
    basePath: '/verander-mijn-plan',
    nl: '/nl/verander-mijn-plan',
    en: '/en/change-my-recovery-plan',
    type: 'private',
    component: PageChangeRecoveryPlan,
  },
  resetPassword: {
    basePath: '/user/reset/:uid/:timestamp/:hash',
    nl: '/nl/user/reset/:uid/:timestamp/:hash',
    en: '/en/user/reset/:uid/:timestamp/:hash',
    type: 'default',
    component: PageResetPassword,
  },
  resetPasswordLogin: {
    basePath: '/user/reset/:uid/:timestamp/:hash/login',
    nl: '/nl/user/reset/:uid/:timestamp/:hash/login',
    en: '/en/user/reset/:uid/:timestamp/:hash/login',
    type: 'default',
    component: PageResetPassword,
  },
  resetPasswordRequest: {
    basePath: '/wachtwoord-vergeten',
    nl: '/nl/wachtwoord-vergeten',
    en: '/en/forgot-password',
    type: 'default',
    component: PageResetPassword,
  },
  validateEmail: {
    basePath: '/validate/:uid/:timestamp/:hash',
    nl: '/nl/validate/:uid/:timestamp/:hash',
    en: '/en/validate/:uid/:timestamp/:hash',
    type: 'default',
    component: PageValidateEmail,
  },
  calendar: {
    basePath: '/kalender',
    nl: '/nl/kalender',
    en: '/en/calendar',
    type: 'private',
    component: PageCalendar,
  },
};

export default routesConfigSelfHelp;
